import { useEffect, useRef, type Dispatch, type RefObject } from "react"

import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  useAgeGroups,
  usePixels,
  useRegions,
} from "@/api/hooks/campaignForm"

import { AgeGroupChips } from "../widgets/AgeGroupChips"
import { InterestsBehaviorsPicker } from "../widgets/InterestsBehaviorsPicker"
import { SearchableMultiSelect } from "../widgets/SearchableMultiSelect"
import {
  LEAD_GEN_EXCLUDED_AGE_GROUP,
  minDailyBudget,
  type FormAction,
  type FormState,
} from "../state/formReducer"

/**
 * While a `type="number"` input is focused, browsers use the mouse wheel
 * (and sometimes trackpad scroll) to step the value instead of scrolling
 * the page — often by `step` (here 0.01), so two accidental ticks look
 * like "35 → 34.98" and "0.14 → 0.12". We prevent that and forward scroll.
 */
function useNumberInputScrollInsteadOfStep(ref: RefObject<HTMLInputElement | null>) {
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      if (document.activeElement !== el) return
      e.preventDefault()
      el.blur()
      window.scrollBy({ top: e.deltaY, left: e.deltaX, behavior: "auto" })
    }
    el.addEventListener("wheel", onWheel, { passive: false })
    return () => el.removeEventListener("wheel", onWheel)
  })
}

/**
 * Step 2 — Ad group. Parity: `form.html:1533-1581` +
 * `adgroup.js:188-255`.
 *
 *   - Pixel select with auto-select-when-1 behaviour.
 *   - Regions rendered through the intuitive multi-select with search.
 *   - Age groups as chip buttons, with the `ALL_AGES` exclusivity
 *     handled in the reducer.
 *   - Budget + optional conversion bid.
 */

interface Props {
  state: FormState
  dispatch: Dispatch<FormAction>
  budgetError?: string
  locationsError?: string
  ageGroupsError?: string
}

export function AdGroupStep({
  state,
  dispatch,
  budgetError,
  locationsError,
  ageGroupsError,
}: Props) {
  const budgetInputRef = useRef<HTMLInputElement>(null)
  const bidInputRef = useRef<HTMLInputElement>(null)
  useNumberInputScrollInsteadOfStep(budgetInputRef)
  useNumberInputScrollInsteadOfStep(bidInputRef)

  const pixelsQuery = usePixels(state.advertiserId)
  const regionsQuery = useRegions(state.advertiserId, state.objectiveType)
  const agesQuery = useAgeGroups(Boolean(state.objectiveType))

  // Auto-select pixel when exactly one is returned — parity with
  // `adgroup.js:206`.
  useEffect(() => {
    if (!pixelsQuery.data) return
    if (pixelsQuery.data.length === 1 && state.pixelId === null) {
      dispatch({ type: "SET_PIXEL", id: pixelsQuery.data[0].id })
    }
  }, [pixelsQuery.data, state.pixelId, dispatch])

  const pixelOptions = pixelsQuery.data ?? []
  const selectedPixelStillListed = pixelOptions.some(
    (p) => p.id === state.pixelId,
  )

  const regionOptions = (regionsQuery.data ?? []).map((r) => ({
    id: r.id,
    name: r.name,
  }))
  const minBudget = minDailyBudget(state)

  return (
    <section className="rounded-lg border border-[#5a5a5a] bg-[#2d2d2d] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]">
      <h3 className="mb-3 text-lg font-semibold">
        🎨 Step 2: Ad Group Settings
      </h3>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
        <div className="min-w-0 flex-1 sm:max-w-xs">
          <Label>Pixel:</Label>
          <div className="relative">
            {/* Shadow input for native required validation on Radix Select —
                see IdentityPicker for the pattern. */}
            <input
              tabIndex={-1}
              aria-hidden="true"
              required
              value={state.pixelId ?? ""}
              onChange={() => {}}
              className="pointer-events-none absolute inset-0 h-full w-full opacity-0"
            />
            <Select
              value={state.pixelId ?? ""}
              onValueChange={(value) =>
                dispatch({ type: "SET_PIXEL", id: value || null })
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select pixel..." />
              </SelectTrigger>
              <SelectContent>
                {state.pixelId && !selectedPixelStillListed ? (
                  <SelectItem value={state.pixelId}>
                    {state.pixelId} (cached)
                  </SelectItem>
                ) : null}
                {pixelOptions.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {pixelsQuery.isError ? (
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
              <p className="text-xs text-amber-400">
                {pixelOptions.length > 0
                  ? "Couldn’t refresh pixels from TikTok — keeping the last list."
                  : "Couldn’t load pixels from TikTok."}
              </p>
              <button
                type="button"
                disabled={pixelsQuery.isFetching}
                onClick={() => void pixelsQuery.refetch()}
                className="text-[11px] font-semibold text-[#9eabff] underline-offset-2 hover:underline disabled:opacity-50"
              >
                {pixelsQuery.isFetching ? "Retrying…" : "Retry"}
              </button>
            </div>
          ) : null}
        </div>

        <div className="min-w-0 flex-1 sm:max-w-sm">
          <Label>Ad Group Name:</Label>
          <Input
            type="text"
            required
            value={state.adgroupName}
            onChange={(e) =>
              dispatch({ type: "SET_ADGROUP_NAME", value: e.target.value })
            }
          />
        </div>
      </div>

      <div className="mt-4" data-form-field="locations">
        <Label>Locations:</Label>
        <SearchableMultiSelect
          options={regionOptions}
          selected={state.selectedRegions}
          onToggle={(id) => dispatch({ type: "TOGGLE_REGION", id })}
          onClear={() => dispatch({ type: "SET_REGIONS", ids: [] })}
          searchPlaceholder="Enter your target location"
          emptyPlaceholder="Search locations..."
          emptyListText="No countries available"
        />
        {locationsError ? (
          <p className="mt-1 text-xs text-red-400">{locationsError}</p>
        ) : null}
      </div>

      <div className="mt-4" data-form-field="ageGroups">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
          <Label className="mb-0 shrink-0">Age Groups:</Label>
          <AgeGroupChips
            ages={agesQuery.data}
            selected={state.selectedAgeGroups}
            onToggle={(id) => dispatch({ type: "TOGGLE_AGE_GROUP", id })}
            hiddenIds={
              state.objectiveType === "LEAD_GENERATION"
                ? [LEAD_GEN_EXCLUDED_AGE_GROUP]
                : []
            }
          />
        </div>
        {ageGroupsError ? (
          <p className="mt-1 text-xs text-red-400">{ageGroupsError}</p>
        ) : null}
      </div>

      <InterestsBehaviorsPicker state={state} dispatch={dispatch} />

      <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
        <div data-form-field="budget">
          <Label>{`Daily Budget (min. $${minBudget})`}</Label>
          <Input
            ref={budgetInputRef}
            type="number"
            step="0.01"
            min={minBudget}
            required
            value={state.budget}
            className={budgetError ? "border-red-500 focus-visible:ring-red-500" : ""}
            onChange={(e) =>
              dispatch({ type: "SET_BUDGET", value: e.target.value })
            }
            onBlur={() => {
              const raw = state.budget.trim()
              if (!raw) return
              const parsed = Number(raw)
              if (!Number.isFinite(parsed) || parsed < minBudget) {
                dispatch({ type: "SET_BUDGET", value: String(minBudget) })
              }
            }}
          />
          {budgetError ? (
            <p className="mt-1 text-xs text-red-400">{budgetError}</p>
          ) : null}
        </div>
        <div>
          <Label>
            Bid Amount ($){" "}
            <span className="text-white">(Optional)</span>
          </Label>
          <Input
            ref={bidInputRef}
            type="number"
            step="0.01"
            min="0.01"
            value={state.conversionBidPrice}
            onChange={(e) =>
              dispatch({ type: "SET_CONVERSION_BID", value: e.target.value })
            }
          />
        </div>
      </div>
    </section>
  )
}
